/**
 * PageHelpButton: the "?" at the top right of a page header.
 *
 * The page's ONLY "?" (tabs have none of their own: TabHelp draws nothing).
 * Menu: Take the tour, Help for this page, Help for the {tab} tab (when the page reports an open tab with
 * useHelpActiveTab), Ask AI (when it is switched on; admins and help editors
 * see why it is missing), Reset tours for this page, the "Open tours by themselves in this app" switch
 * (on by default; a Skip on a tour that opened by itself switches it off for the whole app) and, for
 * help editors, Edit help for this page. It also tells the help framework which page is on screen and
 * auto-starts the page's tour on a first visit.
 *
 * Usage: <PageHelpButton pageKey="events.sources" />
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconBook2, IconHelp, IconLayoutNavbar, IconPencil, IconRefresh, IconRoute, IconSparkles, IconBulbOff } from '@tabler/icons-react';
import { Box, Menu, Switch, Text, Tooltip } from '@/components/ui';
import ActionIcon from '@/components/ui/ActionIcon';
import { useHelpContext } from './HelpContext';
import { findTabHelp, usePageHelp } from './usePageHelp';
import { activeTours, appKeyOfPageKey, primaryTour } from './helpUtils';
import { AI_FEATURE_FLAGS_PATH, HELP_ADMIN_PATH } from './helpPaths';

export interface PageHelpButtonProps {
  pageKey: string;
}

export function PageHelpButton({ pageKey }: PageHelpButtonProps) {
  const ctx = useHelpContext();
  const navigate = useNavigate();
  const { page, def, isLoading } = usePageHelp(pageKey);

  const registerPage = ctx?.registerPage;
  useEffect(() => (registerPage ? registerPage(pageKey) : undefined), [registerPage, pageKey]);

  const autoStartIfDue = ctx?.autoStartIfDue;
  useEffect(() => {
    if (page && autoStartIfDue) autoStartIfDue(page);
  }, [page, autoStartIfDue]);

  if (!ctx || !ctx.ready) return null;

  const tour = primaryTour(page);
  const hasTours = activeTours(page).length > 0;
  const appKey = page?.appKey || def?.appKey || appKeyOfPageKey(pageKey);
  const autoStartOn = ctx.appAutoStart(appKey);
  const activeTab = ctx.activeTabFor(pageKey);
  const { tab: tabHelp, tabDef } = findTabHelp(page, def, activeTab);
  const tabTitle = tabHelp?.title || tabDef?.title || null;

  return (
    <Menu position="bottom-end" withinPortal shadow="md" width={270}>
      <Menu.Target>
        <Tooltip label="Help for this page" withArrow>
          <ActionIcon variant="light" radius="xl" size="lg" aria-label="Help for this page" data-help-id={`${pageKey}.help`}>
            <IconHelp size={20} />
          </ActionIcon>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Help</Menu.Label>
        <Menu.Item leftSection={<IconRoute size={16} />} disabled={!tour} onClick={() => tour && void ctx.startTour(tour)}>
          Take the tour
        </Menu.Item>
        {!tour && (
          <Text size="xs" c="dimmed" px="sm" pb={6}>
            {isLoading ? 'Loading help.' : 'No tour for this page yet.'}
          </Text>
        )}
        <Menu.Item leftSection={<IconBook2 size={16} />} onClick={() => ctx.openHelp(pageKey, null)}>
          Help for this page
        </Menu.Item>
        {activeTab && (
          <Menu.Item leftSection={<IconLayoutNavbar size={16} />} onClick={() => ctx.openHelp(pageKey, activeTab)}>
            {tabTitle ? `Help for the ${tabTitle} tab` : 'Help for this tab'}
          </Menu.Item>
        )}
        {ctx.ask.enabled ? (
          <Menu.Item leftSection={<IconSparkles size={16} />} onClick={() => ctx.openHelp(pageKey, undefined, { focusAsk: true })}>
            Ask AI
          </Menu.Item>
        ) : ctx.canManageHelp && ctx.ask.loaded ? (
          <>
            <Menu.Item leftSection={<IconBulbOff size={16} />} disabled>
              Ask AI is switched off
            </Menu.Item>
            {ctx.isAdmin ? (
              <Menu.Item onClick={() => navigate(AI_FEATURE_FLAGS_PATH)}>
                <Text size="xs" c="dimmed">
                  Switch on HelpAssistant in Feature Flags
                </Text>
              </Menu.Item>
            ) : (
              <Text size="xs" c="dimmed" px="sm" pb={6}>
                An admin can switch on HelpAssistant in AI Management › Feature Flags.
              </Text>
            )}
          </>
        ) : null}
        <Menu.Divider />
        <Menu.Item leftSection={<IconRefresh size={16} />} disabled={!hasTours || !page} onClick={() => page && void ctx.resetToursForPage(page)}>
          Reset tours for this page
        </Menu.Item>
        {/* A real switch (not a menu item), so clicking it keeps the menu open and screen readers hear its state. */}
        <Box px="sm" py={6}>
          <Switch
            size="xs"
            checked={autoStartOn}
            disabled={!page}
            onChange={(e) => ctx.setAppAutoStart(appKey, e.currentTarget.checked, { pageKey })}
            label="Open tours by themselves in this app"
          />
        </Box>
        {ctx.canManageHelp && (
          <Menu.Item leftSection={<IconPencil size={16} />} onClick={() => navigate(`${HELP_ADMIN_PATH}?page=${encodeURIComponent(pageKey)}`)}>
            Edit help for this page
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
}
